import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Camera, Play, Instagram, Star, Truck, RefreshCw, Shield, Eye, Image, Plus, Menu, X, ShoppingBag, User, Search, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 14: STUDIO
 * Style: Visual Portfolio, Photography-focused, Creative
 * Best for: Designer portfolios, Lookbook showcases, Fashion photography brands
 * Light theme with violet/purple creative accents
 */
const Template14 = () => {
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    const toggleWishlist = (id) => setWishlist(prev => ({ ...prev, [id]: !prev[id] }));

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial(prev =>
                diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length
            );
        }
    };

    // Mobile data arrays
    const categories = [
        { title: 'Street Kurtas', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=600&h=800&fit=crop', count: '42 styles', color: 'from-orange-500/80' },
        { title: 'Fusion Wear', image: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=600&h=800&fit=crop', count: '38 styles', color: 'from-pink-500/80' },
        { title: 'Denim & Desi', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=800&fit=crop', count: '55 styles', color: 'from-yellow-500/80' },
        { title: 'Block Print', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&h=800&fit=crop', count: '29 styles', color: 'from-emerald-500/80' },
    ];

    const mobileProducts = [
        { id: 1, name: 'Bandhani Crop Top', price: 1499, oldPrice: 2199, image: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=500&h=650&fit=crop', badge: 'Trending' },
        { id: 2, name: 'Mirror Work Jacket', price: 2999, oldPrice: null, image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=650&fit=crop', badge: 'New Drop' },
        { id: 3, name: 'Ikat Palazzo Set', price: 1899, oldPrice: 2499, image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=650&fit=crop', badge: 'Hot' },
        { id: 4, name: 'Phulkari Denim Jacket', price: 3499, oldPrice: null, image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=500&h=650&fit=crop', badge: null },
        { id: 5, name: 'Tie-Dye Kurta', price: 999, oldPrice: 1499, image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', badge: 'Sale' },
        { id: 6, name: 'Kalamkari Bomber', price: 4299, oldPrice: null, image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=500&h=650&fit=crop', badge: 'Limited' },
    ];

    const testimonials = [
        { text: 'The attention to detail in their designs is remarkable. Each piece feels like a work of art.', name: 'Priya Sharma', role: 'Fashion Blogger', location: 'Mumbai' },
        { text: 'Studio\'s lookbooks are my go-to inspiration. Their aesthetic is truly unique.', name: 'Ananya Iyer', role: 'Stylist', location: 'Bangalore' },
        { text: 'Every piece I own from them gets compliments. The quality is consistently outstanding.', name: 'Meera Nair', role: 'Designer', location: 'Chennai' },
    ];

    const features = [
        { icon: Truck, title: 'Free Shipping', desc: 'Above Rs 2,999' },
        { icon: RefreshCw, title: 'Easy Returns', desc: '15-day policy' },
        { icon: Shield, title: 'Secure Payment', desc: '100% secure' },
        { icon: Camera, title: 'Styling Service', desc: 'Expert advice' },
    ];

    const instagramImages = [
        'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=300&h=300&fit=crop',
    ];

    // Desktop data arrays (from original)
    const portfolio = [
        { id: 1, title: 'Monsoon Collection', category: 'Editorial', image: 'https://placehold.co/600x800/F5F3FF/6D28D9?text=Editorial+1', aspect: 'tall' },
        { id: 2, title: 'Urban Streets', category: 'Streetwear', image: 'https://placehold.co/600x600/EDE9FE/7C3AED?text=Street+1', aspect: 'square' },
        { id: 3, title: 'Festive Dreams', category: 'Occasion', image: 'https://placehold.co/600x400/DDD6FE/8B5CF6?text=Festive+1', aspect: 'wide' },
        { id: 4, title: 'Contemporary Fusion', category: 'Indo-Western', image: 'https://placehold.co/600x800/F5F3FF/6D28D9?text=Fusion+1', aspect: 'tall' },
        { id: 5, title: 'Minimalist Edit', category: 'Basics', image: 'https://placehold.co/600x600/EDE9FE/7C3AED?text=Minimal+1', aspect: 'square' },
        { id: 6, title: 'Bridal Couture', category: 'Wedding', image: 'https://placehold.co/600x800/DDD6FE/8B5CF6?text=Bridal+1', aspect: 'tall' },
        { id: 7, title: 'Resort Wear', category: 'Summer', image: 'https://placehold.co/600x400/F5F3FF/6D28D9?text=Resort+1', aspect: 'wide' },
        { id: 8, title: 'Office Elegance', category: 'Workwear', image: 'https://placehold.co/600x600/EDE9FE/7C3AED?text=Office+1', aspect: 'square' }
    ];

    const desktopProducts = [
        { id: 1, name: 'Printed Silk Kaftan', price: '\u20B94,999', image: 'https://placehold.co/400x500/F5F3FF/6D28D9?text=Kaftan+1' },
        { id: 2, name: 'Embroidered Jacket', price: '\u20B96,499', image: 'https://placehold.co/400x500/EDE9FE/7C3AED?text=Jacket+1' },
        { id: 3, name: 'Asymmetric Kurta', price: '\u20B93,299', image: 'https://placehold.co/400x500/DDD6FE/8B5CF6?text=Kurta+1' },
        { id: 4, name: 'Fusion Dress', price: '\u20B95,799', image: 'https://placehold.co/400x500/F5F3FF/6D28D9?text=Dress+1' }
    ];

    const behindScenes = [
        { title: 'Design Process', image: 'https://placehold.co/400x300/F5F3FF/6D28D9?text=Process' },
        { title: 'Fabric Selection', image: 'https://placehold.co/400x300/EDE9FE/7C3AED?text=Fabric' },
        { title: 'Photo Shoot', image: 'https://placehold.co/400x300/DDD6FE/8B5CF6?text=Shoot' }
    ];

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* === MOBILE HEADER === */}
            <div className="lg:hidden">
                <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white text-center py-2 text-[10px] sm:text-xs font-bold tracking-wide px-4">
                    EXPLORE OUR NEW VISUAL PORTFOLIO COLLECTION
                </div>
                <header className="bg-white sticky top-0 z-50 border-b border-violet-100">
                    <div className="px-4 sm:px-6">
                        <div className="flex items-center justify-between py-3 sm:py-4">
                            <button className="min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                                {mobileMenuOpen ? <X className="w-5 h-5 text-violet-900" /> : <Menu className="w-5 h-5 text-violet-900" />}
                            </button>
                            <h1 onClick={() => navigate('/template14')} className="text-xl sm:text-2xl font-light text-violet-950 cursor-pointer tracking-wider">
                                STUDIO
                            </h1>
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="relative cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => navigate('/template14/cart')}>
                                    <ShoppingBag className="w-4 h-4 text-violet-800" />
                                    <span className="absolute top-2 right-0 w-4 h-4 bg-violet-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">0</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {mobileMenuOpen && (
                        <div className="bg-white border-t border-violet-100">
                            <nav className="px-4 py-6 space-y-1">
                                {['Home', 'Shop', 'Contact', 'Orders'].map(item => (
                                    <button key={item} onClick={() => { navigate(`/template14${item === 'Home' ? '' : '/' + item.toLowerCase()}`); setMobileMenuOpen(false); }} className="block w-full text-left text-base font-medium text-violet-800 py-3 min-h-[44px]">{item}</button>
                                ))}
                                <div className="flex items-center gap-6 pt-4 border-t border-violet-100">
                                    <Search className="w-5 h-5 text-violet-800 cursor-pointer" />
                                    <Heart className="w-5 h-5 text-violet-800 cursor-pointer" />
                                    <User className="w-5 h-5 text-violet-800 cursor-pointer" onClick={() => navigate('/template14/profile')} />
                                </div>
                            </nav>
                        </div>
                    )}
                </header>
            </div>

            {/* === HERO === */}
            {/* Mobile Hero - stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img src="https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=1920&q=80" alt="Studio Collection" className="w-full h-full object-cover" />
                </div>
                <div className="bg-gradient-to-b from-violet-50 to-white px-5 py-8">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-full mb-3 text-xs font-medium">
                        <Camera className="w-3 h-3" /> Visual Portfolio
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-light text-violet-950 leading-tight mb-3">
                        Where Fashion<br/><span className="font-serif italic text-violet-600">Meets Art</span>
                    </h2>
                    <p className="text-[14px] text-violet-700/70 leading-relaxed mb-6">A curated visual journey through contemporary Indian design. Every frame tells a story.</p>
                    <button onClick={() => navigate('/template14/shop')} className="w-full py-4 bg-violet-600 text-white font-medium text-sm tracking-wider rounded-full min-h-[48px] flex items-center justify-center gap-2 hover:bg-violet-700 transition-colors">
                        EXPLORE PORTFOLIO <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop Hero - EXACT original */}
            <section className="hidden lg:block relative min-h-screen overflow-hidden bg-gradient-to-br from-violet-50 via-purple-50/50 to-fuchsia-50/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-16">
                        {/* Text Content */}
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-6">
                                <Camera className="w-4 h-4 text-violet-600" />
                                <span className="text-violet-700 text-sm font-medium">Visual Portfolio</span>
                            </div>
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-violet-950 leading-[1.0] mb-6">
                                Where Fashion
                                <span className="block font-serif italic text-violet-600">Meets Art</span>
                            </h1>
                            <p className="text-violet-700/70 text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                                A curated visual journey through contemporary Indian design.
                                Every frame tells a story, every piece is a statement.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template14/shop')}
                                    className="group px-8 py-4 bg-violet-600 text-white text-sm font-medium tracking-wider uppercase hover:bg-violet-700 transition-all duration-300 rounded-full flex items-center justify-center gap-3"
                                >
                                    Explore Portfolio
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="group px-8 py-4 border-2 border-violet-300 text-violet-700 text-sm font-medium tracking-wider uppercase hover:border-violet-600 transition-all duration-300 rounded-full flex items-center justify-center gap-3">
                                    <Play className="w-4 h-4" />
                                    Watch Lookbook
                                </button>
                            </div>
                        </div>

                        {/* Hero Gallery Grid */}
                        <div className="relative">
                            <div className="grid grid-cols-12 gap-3 max-w-xl mx-auto">
                                <div className="col-span-7 space-y-3">
                                    <div className="aspect-[4/5] overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/400x500/F5F3FF/6D28D9?text=Hero+1"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/400x300/EDE9FE/7C3AED?text=Hero+2"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                </div>
                                <div className="col-span-5 space-y-3 pt-8">
                                    <div className="aspect-square overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/300x300/DDD6FE/8B5CF6?text=Hero+3"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/300x400/F5F3FF/6D28D9?text=Hero+4"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Floating Stats */}
                            <div className="absolute -left-4 bottom-20 bg-white shadow-xl px-5 py-4 rounded-xl z-20">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                                        <Image className="w-5 h-5 text-violet-600" />
                                    </div>
                                    <div>
                                        <p className="text-violet-950 font-bold">200+</p>
                                        <p className="text-violet-600 text-xs">Looks Created</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* === CATEGORIES (Mobile only) === */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="px-5 mb-8">
                    <div className="text-center">
                        <span className="text-violet-600 text-xs font-medium tracking-[0.2em] uppercase">The Gallery</span>
                        <h2 className="text-xl sm:text-2xl font-light text-violet-950 mt-2">Visual Stories</h2>
                    </div>
                </div>
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                    {categories.map((cat, i) => (
                        <div key={i} className="w-[68vw] flex-shrink-0 snap-start cursor-pointer group">
                            <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                                <img src={cat.image} alt={cat.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <div className={`absolute inset-0 bg-gradient-to-t from-violet-900/60 to-transparent`} />
                                <div className="absolute bottom-4 left-4 right-4">
                                    <h3 className="text-white font-medium text-lg mb-0.5">{cat.title}</h3>
                                    <p className="text-violet-200 text-xs font-medium">{cat.count}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
            </section>

            {/* === PORTFOLIO GALLERY (Desktop only) - EXACT original === */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">The Gallery</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3">Visual Stories</h2>
                        <p className="text-violet-600/70 mt-4 max-w-xl mx-auto">
                            A curated collection of our finest work, capturing the essence of contemporary Indian fashion
                        </p>
                    </div>

                    {/* Masonry Grid */}
                    <div className="columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
                        {portfolio.map((item) => (
                            <div
                                key={item.id}
                                className="break-inside-avoid group cursor-pointer"
                                onClick={() => setSelectedImage(item)}
                            >
                                <div className={`relative overflow-hidden rounded-xl bg-violet-100 ${
                                    item.aspect === 'tall' ? 'aspect-[3/4]' :
                                    item.aspect === 'wide' ? 'aspect-[4/3]' : 'aspect-square'
                                }`}>
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-violet-900/0 group-hover:bg-violet-900/40 transition-colors duration-300" />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <Eye className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-violet-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <p className="text-violet-200 text-xs uppercase tracking-wider">{item.category}</p>
                                        <p className="text-white font-medium">{item.title}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === QUOTE SECTION (Desktop only) - EXACT original === */}
            <section className="hidden lg:block py-20 lg:py-28 bg-violet-600 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-3xl lg:text-4xl font-light leading-relaxed">
                        "Fashion is the armor to survive the reality of everyday life.
                        We create that armor with <span className="font-serif italic">art and passion</span>."
                    </p>
                    <p className="text-violet-200 mt-8">— Our Design Philosophy</p>
                </div>
            </section>

            {/* === PRODUCTS === */}
            {/* Mobile Products - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="px-5 mb-8">
                    <div className="flex items-end justify-between">
                        <div>
                            <span className="text-violet-600 text-xs font-medium tracking-[0.2em] uppercase">Shop The Edit</span>
                            <h2 className="text-xl sm:text-2xl font-light text-violet-950 mt-2">Featured Pieces</h2>
                        </div>
                    </div>
                </div>
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                    {mobileProducts.map((p) => (
                        <div key={p.id} className="w-[44vw] flex-shrink-0 snap-start">
                            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-violet-50 mb-2.5">
                                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                {p.badge && <span className="absolute top-2 left-2 bg-violet-600 text-white text-[10px] px-2.5 py-1 rounded-full font-medium">{p.badge}</span>}
                                <button onClick={() => toggleWishlist(p.id)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                    <Heart className={`w-3.5 h-3.5 ${wishlist[p.id] ? 'fill-violet-500 text-violet-500' : 'text-violet-600'}`} />
                                </button>
                                <button className="absolute bottom-2 right-2 w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center">
                                    <Plus className="w-3.5 h-3.5 text-white" />
                                </button>
                            </div>
                            <h3 className="text-xs font-medium text-violet-950 line-clamp-1 mb-0.5">{p.name}</h3>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-violet-950">Rs {p.price.toLocaleString()}</span>
                                {p.oldPrice && <span className="text-[10px] text-violet-400 line-through">Rs {p.oldPrice.toLocaleString()}</span>}
                            </div>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
                <div className="text-center mt-6 px-5">
                    <button onClick={() => navigate('/template14/shop')} className="w-full py-3 border-2 border-violet-300 text-violet-700 font-medium text-sm tracking-wider rounded-full min-h-[48px]">VIEW ALL PIECES</button>
                </div>
            </section>

            {/* Desktop Products - EXACT original */}
            <section className="hidden lg:block py-16 lg:py-24 bg-violet-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">Shop The Edit</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3">Featured Pieces</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template14/shop')}
                            className="group flex items-center gap-2 text-violet-700 font-medium mt-4 lg:mt-0 hover:text-violet-900 transition-colors"
                        >
                            View All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {desktopProducts.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template14/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-violet-100 rounded-xl mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                        <Heart className="w-4 h-4 text-violet-600" />
                                    </button>
                                </div>
                                <h3 className="text-violet-950 font-medium group-hover:text-violet-600 transition-colors">
                                    {product.name}
                                </h3>
                                <p className="text-violet-600 font-semibold mt-1">{product.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === BRAND STORY / BEHIND THE SCENES === */}
            {/* Mobile Brand Story - full-bleed stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/3]">
                    <img src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1920&q=80" alt="Behind The Scenes" className="w-full h-full object-cover" />
                </div>
                <div className="px-5 py-8 bg-gradient-to-b from-violet-50 to-white">
                    <span className="text-violet-600 text-xs font-medium tracking-[0.2em] uppercase">Behind The Scenes</span>
                    <h2 className="text-xl font-light text-violet-950 mt-2 mb-3">The Creative Process</h2>
                    <p className="text-[14px] text-violet-700/80 leading-relaxed mb-5">Every collection begins with a story. From mood boards to fabric selection, from sketches to final stitches -- we invite you into our world of creativity.</p>
                    <button onClick={() => navigate('/template14/contact')} className="text-sm text-violet-700 font-medium border-b border-violet-300 pb-1 min-h-[44px] inline-flex items-center">Explore Our Story</button>
                </div>
            </section>

            {/* Desktop Behind The Scenes - EXACT original */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div>
                            <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">Behind The Scenes</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3 mb-6">
                                The Creative Process
                            </h2>
                            <p className="text-violet-700/80 text-lg leading-relaxed mb-8">
                                Every collection begins with a story. From mood boards to fabric selection,
                                from sketches to final stitches — we invite you into our world of creativity.
                            </p>
                            <p className="text-violet-700/80 leading-relaxed mb-8">
                                Our studio in Mumbai houses a team of passionate designers, artisans,
                                and storytellers who collaborate to bring each vision to life.
                            </p>
                            <button
                                onClick={() => navigate('/template14/shop')}
                                className="group inline-flex items-center gap-3 text-violet-700 font-medium hover:text-violet-900 transition-colors"
                            >
                                Explore Our Story
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {behindScenes.map((scene, index) => (
                                <div
                                    key={index}
                                    className={`group cursor-pointer ${index === 2 ? 'col-span-2' : ''}`}
                                >
                                    <div className={`relative overflow-hidden rounded-xl ${index === 2 ? 'aspect-video' : 'aspect-square'}`}>
                                        <img
                                            src={scene.image}
                                            alt={scene.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-violet-900/20 group-hover:bg-violet-900/40 transition-colors" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Play className="w-12 h-12 text-white opacity-70 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                        <p className="absolute bottom-4 left-4 text-white font-medium">{scene.title}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* === FEATURES === */}
            {/* Mobile Features - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-violet-50">
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide">
                    {features.map((f, i) => (
                        <div key={i} className="min-w-[150px] flex-shrink-0 snap-start text-center py-5 px-4 bg-white rounded-2xl">
                            <div className="w-11 h-11 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-2.5">
                                <f.icon className="w-5 h-5 text-violet-600" />
                            </div>
                            <h3 className="text-xs font-medium text-violet-950 mb-0.5">{f.title}</h3>
                            <p className="text-[10px] text-violet-600">{f.desc}</p>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
            </section>

            {/* Desktop Services - EXACT original */}
            <section className="hidden lg:block py-12 bg-violet-50 border-t border-violet-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Free Shipping</p>
                                <p className="text-violet-600 text-sm">Above ₹2,999</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Easy Returns</p>
                                <p className="text-violet-600 text-sm">15-day policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Secure Payment</p>
                                <p className="text-violet-600 text-sm">100% secure</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Camera className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Styling Service</p>
                                <p className="text-violet-600 text-sm">Expert advice</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* === TESTIMONIALS === */}
            {/* Mobile Testimonials - swipeable */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="px-5 mb-8">
                    <div className="text-center">
                        <span className="text-violet-600 text-xs font-medium tracking-[0.2em] uppercase">Voices</span>
                        <h2 className="text-xl sm:text-2xl font-light text-violet-950 mt-2">What They Say</h2>
                    </div>
                </div>
                <div className="overflow-hidden px-5" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                    <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                        {testimonials.map((t, i) => (
                            <div key={i} className="w-full flex-shrink-0 px-2">
                                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl p-6 text-center">
                                    <div className="flex justify-center gap-1 mb-3">
                                        {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-violet-400 text-violet-400" />)}
                                    </div>
                                    <p className="text-[14px] text-violet-800/80 leading-relaxed mb-4 italic">"{t.text}"</p>
                                    <p className="text-sm font-medium text-violet-950">{t.name}</p>
                                    <p className="text-xs text-violet-600">{t.role}</p>
                                    <p className="text-[10px] text-violet-400">{t.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-center gap-2 mt-5">
                        {testimonials.map((_, i) => (
                            <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-2 rounded-full transition-all ${activeTestimonial === i ? 'w-6 bg-violet-500' : 'w-2 bg-violet-200'}`} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Testimonials - EXACT original */}
            <section className="hidden lg:block py-16 lg:py-24 bg-gradient-to-br from-violet-50 to-fuchsia-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">Voices</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3">What They Say</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {testimonials.slice(0, 2).map((testimonial, index) => (
                            <div key={index} className="bg-white p-8 lg:p-10 rounded-2xl shadow-sm">
                                <div className="flex gap-1 mb-6">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-violet-400 text-violet-400" />
                                    ))}
                                </div>
                                <p className="text-violet-800/80 text-lg leading-relaxed mb-8 italic">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center">
                                        <span className="text-violet-600 font-semibold text-lg">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-violet-950">{testimonial.name}</p>
                                        <p className="text-violet-600 text-sm">{testimonial.role}</p>
                                        <p className="text-violet-400 text-xs">{testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === NEWSLETTER === */}
            {/* Mobile Newsletter - stacked rounded */}
            <section className="lg:hidden py-10 sm:py-16 bg-violet-950 text-white">
                <div className="max-w-2xl mx-auto px-5 text-center">
                    <Camera className="w-8 h-8 text-violet-400 mx-auto mb-4" />
                    <h3 className="text-xl sm:text-2xl font-light mb-3">Join The Studio</h3>
                    <p className="text-[14px] text-violet-300 mb-6">Get exclusive access to new lookbooks and behind-the-scenes content.</p>
                    <div className="flex flex-col gap-3">
                        <input type="email" placeholder="Enter your email" className="flex-1 px-5 py-4 bg-violet-900 border border-violet-700 rounded-2xl text-white placeholder-violet-400 focus:outline-none focus:border-violet-500 text-sm min-h-[48px]" />
                        <button className="px-8 py-4 bg-violet-500 text-white text-sm font-semibold tracking-wider uppercase hover:bg-violet-400 transition-colors rounded-2xl min-h-[48px]">Subscribe</button>
                    </div>
                </div>
            </section>

            {/* Desktop Newsletter - EXACT original */}
            <section className="hidden lg:block py-16 lg:py-24 bg-violet-950 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Camera className="w-10 h-10 text-violet-400 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
                        Join The Studio
                    </h2>
                    <p className="text-violet-300 text-lg mb-8">
                        Get exclusive access to new lookbooks, behind-the-scenes content, and first looks at upcoming collections.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-violet-900 border border-violet-700 rounded-full text-white placeholder-violet-400 focus:outline-none focus:border-violet-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-violet-500 text-white text-sm font-semibold tracking-wider uppercase hover:bg-violet-400 transition-colors rounded-full whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* === INSTAGRAM - shared with responsive classes === */}
            <section className="py-10 sm:py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <Instagram className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
                            <span className="text-violet-600 font-medium text-sm">@studio_fashion</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-violet-950">Follow The Journey</h2>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 sm:gap-2 lg:gap-4">
                        {instagramImages.map((img, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer">
                                <img
                                    src={img}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-violet-600/0 group-hover:bg-violet-600/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === FOOTER === */}
            {/* Mobile Footer */}
            <footer className="lg:hidden bg-violet-950 text-white">
                <div className="px-4 sm:px-6 py-10 sm:py-12">
                    <div className="grid grid-cols-2 gap-6 sm:gap-8">
                        <div className="col-span-2">
                            <h3 className="text-xl font-light tracking-wider mb-4">STUDIO</h3>
                            <p className="text-violet-300 text-sm leading-relaxed">Visual stories in contemporary fashion.</p>
                        </div>
                        <div>
                            <h4 className="font-medium mb-3 text-xs uppercase tracking-widest text-violet-300">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                {['Home', 'Shop', 'Contact', 'Orders'].map(item => (
                                    <li key={item}><span onClick={() => navigate(`/template14${item === 'Home' ? '' : '/' + item.toLowerCase()}`)} className="text-violet-400 hover:text-white cursor-pointer transition-colors">{item}</span></li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium mb-3 text-xs uppercase tracking-widest text-violet-300">Help</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-violet-400">Shipping & Returns</li>
                                <li className="text-violet-400">Privacy Policy</li>
                                <li className="text-violet-400">Terms & Conditions</li>
                                <li className="text-violet-400">FAQ</li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-violet-800 mt-8 pt-6">
                        <p className="text-center text-violet-500 text-xs">&copy; 2026 STUDIO. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Desktop Footer - implicit from original (original had no explicit footer, content ended at services) */}
            <footer className="hidden lg:block bg-violet-950 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-xl font-light tracking-wider mb-4">STUDIO</h3>
                            <p className="text-violet-300 text-sm leading-relaxed">Visual stories in contemporary fashion.</p>
                        </div>
                        <div>
                            <h4 className="font-medium mb-4 text-xs uppercase tracking-widest text-violet-300">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                {['Home', 'Shop', 'Contact', 'Orders'].map(item => (
                                    <li key={item}><span onClick={() => navigate(`/template14${item === 'Home' ? '' : '/' + item.toLowerCase()}`)} className="text-violet-400 hover:text-white cursor-pointer transition-colors">{item}</span></li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium mb-4 text-xs uppercase tracking-widest text-violet-300">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-violet-400">Shipping & Returns</li>
                                <li className="text-violet-400">Privacy Policy</li>
                                <li className="text-violet-400">Terms & Conditions</li>
                                <li className="text-violet-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium mb-4 text-xs uppercase tracking-widest text-violet-300">Connect</h4>
                            <p className="text-violet-400 text-sm">hello@studio.com</p>
                        </div>
                    </div>
                    <div className="border-t border-violet-800 mt-12 pt-8">
                        <p className="text-center text-violet-500 text-sm">&copy; 2026 STUDIO. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Image Modal (for desktop portfolio gallery) */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="max-w-4xl w-full">
                        <img
                            src={selectedImage.image}
                            alt={selectedImage.title}
                            className="w-full h-auto rounded-lg"
                        />
                        <div className="mt-4 text-center">
                            <p className="text-violet-300 text-sm uppercase tracking-wider">{selectedImage.category}</p>
                            <p className="text-white text-xl font-medium mt-1">{selectedImage.title}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Template14;